import ELECTRON_MESSAGE_TYPES from '~/constants/ELECTRON_MESSAGE_TYPES';

type MessageTypeNames =
  (typeof ELECTRON_MESSAGE_TYPES)[keyof typeof ELECTRON_MESSAGE_TYPES];

interface ICheckResult<T extends MessageTypeNames, U> {
  type: T;
  data?: U;
  error?: string;
}

export interface IUnknownMessage {
  error: string;
}

export interface IDisplay {
  id?: number;
  heightInPx?: number;
  widthInPx?: number;
  heightInCm?: number;
  widthInCm?: number;
}

export type ElectronAppVersionResult = ICheckResult<
  'ELECTRON_APP_VERSION',
  { version: string }
>;
export type ElectronExternalStorageResult = ICheckResult<
  'EXTERNAL_STORAGE',
  { removableStorageUnitCount: number }
>;
export type ElectronDeviceIdResult = ICheckResult<
  'DEVICE_ID',
  { deviceId: string }
>;
export type ElectronDisplayInfoResult = ICheckResult<
  'DISPLAY_INFO',
  IDisplay[]
>;
export type ElectronOsVersionResult = ICheckResult<
  'OS_VERSION',
  { osVersion: string }
>;
export type ElectronKioskModeResult = ICheckResult<
  'KIOSK_MODE',
  { isKiosk: boolean }
>;
export type ElectronOpenApps = ICheckResult<'OPEN_APPS', { apps: string[] }>;

export interface IElectronMessageResponse {
  [ELECTRON_MESSAGE_TYPES.DISPLAY_INFO]: ElectronDisplayInfoResult;
  [ELECTRON_MESSAGE_TYPES.EXTERNAL_STORAGE]: ElectronExternalStorageResult;
  [ELECTRON_MESSAGE_TYPES.DEVICE_ID]: ElectronDeviceIdResult;
  [ELECTRON_MESSAGE_TYPES.ELECTRON_APP_VERSION]: ElectronAppVersionResult;
  [ELECTRON_MESSAGE_TYPES.KIOSK_MODE]: ElectronKioskModeResult;
  [ELECTRON_MESSAGE_TYPES.OS_VERSION]: ElectronOsVersionResult;
  [ELECTRON_MESSAGE_TYPES.OPEN_APPS]: ElectronOpenApps;
}

const fetchFromElectron = async <T extends MessageTypeNames>(
  messageType: T,
  prefix = 'fetch:',
): Promise<IElectronMessageResponse[T]> => {
  if (!window.electron) {
    throw new Error('Electron runtime not available');
  }

  const response = (await window.electron.invoke(`${prefix}${messageType}`)) as
    | IElectronMessageResponse[T]
    | IUnknownMessage;

  if (!response) {
    throw new Error('No response from Electron');
  }

  if (!('type' in response)) {
    // Throw error for IUnknownMessage response
    throw new Error(`Unexpected response from Electron: ${response.error}`);
  }

  return response;
};

export default fetchFromElectron;
